import React, { useEffect, useId, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const mdComponents = {
  p: ({ children }) => <p className="leading-relaxed [&:not(:first-child)]:mt-2">{children}</p>,
  ul: ({ children }) => <ul className="mt-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="mt-2 list-decimal space-y-1 pl-5">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">{children}</a>
  ),
  code: ({ children }) => <code className="rounded bg-black/5 px-1 py-0.5 text-[0.88em]">{children}</code>,
  blockquote: ({ children }) => <blockquote className="mt-2 border-l-2 border-current/20 pl-3 italic">{children}</blockquote>,
};

export const Md = ({ children, className = '' }) => (
  <div className={className}>
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{children || ''}</ReactMarkdown>
  </div>
);

let mermaidReady;
const loadMermaid = () => {
  mermaidReady = mermaidReady || import('mermaid').then(({ default: m }) => {
    m.initialize({
      startOnLoad: false,
      securityLevel: 'strict',
      theme: 'base',
      fontFamily: 'inherit',
      themeVariables: {
        primaryColor: '#FFFFFF', primaryBorderColor: '#9AA3AD', primaryTextColor: '#1F2630',
        lineColor: '#9AA3AD', secondaryColor: '#F5F6F3', tertiaryColor: '#FFFFFF', fontSize: '15px',
      },
      flowchart: { curve: 'basis', htmlLabels: true, nodeSpacing: 28, rankSpacing: 40, wrappingWidth: 260, padding: 10 },
    });
    return m;
  });
  return mermaidReady;
};

export const Mermaid = ({ source }) => {
  const [svg, setSvg] = useState('');
  const [error, setError] = useState('');
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  useEffect(() => {
    let alive = true;
    setError('');
    loadMermaid()
      .then((m) => m.render(`mmd${uid}${Math.random().toString(36).slice(2)}`, source))
      .then(({ svg: out }) => { if (alive) setSvg(out); })
      .catch((e) => { if (alive) setError(String(e?.message || e)); });
    return () => { alive = false; };
  }, [source, uid]);
  if (error) {
    return (
      <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
        This diagram has a syntax error: {error.split('\n')[0]}
        <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-stone-600">{source}</pre>
      </div>
    );
  }
  if (!svg) return <div className="h-40 animate-pulse rounded-lg bg-black/5" />;
  return <div className="flex justify-center overflow-x-auto [&_svg]:max-w-full" dangerouslySetInnerHTML={{ __html: svg }} />;
};
